import React, { useState, useMemo, useCallback, useId, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import {
  srOnlySx,
  dialogSx,
  dialogPaperSx,
  getDialogTitleSx,
  subtitleSx,
  closeButtonSx,
  textFieldLabelSx,
  textFieldInputSx,
  textFieldUnderlineSx,
  textFieldErrorUnderlineSx,
  helperTextSx,
  step1ContentSx,
  leftPanelSx,
  searchFieldSx,
  scrollableListSx,
  regionBoxSx,
  regionNameSx,
  linkButtonSx,
  checkboxSx,
  countryNameSx,
  rightPanelSx,
  selectedPanelTitleSx,
  chipContainerSx,
  chipSx,
  chipLabelStyle,
  footerActionsSx,
  footerCountTextSx,
  iatasContentSx,
  flagImageStyle,
} from './SuperModalRegistration.styles';
import { SvgIcon } from './SellerRegistrationForm';
import { fieldErrorIconSx, fieldErrorStackSx, fieldErrorTextSx } from './SellerRegistrationForm.styles';

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      ref={ref}
      {...props}
      easing={{
        enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
        exit: 'cubic-bezier(0.32, 0.72, 0, 1)'
      }}
      timeout={{ enter: 600, exit: 300 }}
    />
  );
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface Region {
  name: string;
  countries: Country[];
}

export interface AppliedPOSItem {
  code: string;
  name: string;
  flag: string;
  iata: string;
}

// ─── Redux selector (defined outside component to avoid re-creation) ─────────

const selectSellerReg = (state: any) => state.sellerReg;
const selectPos = createSelector([selectSellerReg], (sellerReg: any) => sellerReg.pos);

// Strips parenthetical suffixes, collapses whitespace, lowercases.
// e.g. "Netherlands (the)" → "netherlands",  "Taiwan (Republic of China)" → "taiwan"
const normalizeCountryName = (s: string) =>
  s.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').replace(/\s+/g, ' ').trim();

// ─── Props ────────────────────────────────────────────────────────────────────

interface SuperModalRegistrationProps {
  open: boolean;
  onClose: () => void;
  onNext: (items: AppliedPOSItem[]) => void;
  onOtherSelected?: () => void;
  initialSelected?: string[];
  initialIataValues?: Record<string, string>;
  /** When false, the IATA step is skipped and applied countries get a null IATA. */
  collectIata?: boolean;
}

// ─── ModalHeader Sub-component ─────────────────────────────────────────────────

interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  closeRef: React.Ref<HTMLButtonElement>;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  subtitle,
  onClose,
  closeRef
}) => {
  const { t } = useTranslation();
  return (
    <Box >
      <DialogTitle component={Box}  id="pos-modal-title" sx={{...getDialogTitleSx(!!subtitle)}}>{title}</DialogTitle>

      <IconButton
        aria-label={t('registrationStepsData.modal.closeButton.ariaLabel')}
        ref={closeRef}
        onClick={onClose}
        sx={closeButtonSx}
      >
        <SvgIcon src='/images/registration/closeButton.svg' color="#1460AA" size={14} />
      </IconButton>
      {subtitle && (
        <Typography id="pos-modal-subtitle" sx={subtitleSx}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};
// ─── VisuallyHidden ───────────────────────────────────────────────────────────

interface VisuallyHiddenProps {
  role?: string;
  'aria-live'?: 'polite' | 'off' | 'assertive';
  'aria-atomic'?: boolean;
  children?: React.ReactNode;
}

const VisuallyHidden: React.FC<VisuallyHiddenProps> = ({
  role,
  'aria-live': ariaLive,
  'aria-atomic': ariaAtomic,
  children
}) => (
  <Box role={role} aria-live={ariaLive} aria-atomic={ariaAtomic} sx={srOnlySx}>
    {children}
  </Box>
);

// ─── FlagImage ────────────────────────────────────────────────────────────────

const FlagImage: React.FC<{ flag: string }> = ({ flag }) => {
  if (!flag) return null;
  return <img src={process.env.PUBLIC_URL + flag} alt="" style={flagImageStyle} />;
};

// ─── FieldError ───────────────────────────────────────────────────────────────

const FieldError = ({ message, id }: { message: string; id?: string }) => (
  <Stack
    role="alert"
    direction="row"
    alignItems="center"
    spacing={0.5}
    sx={fieldErrorStackSx}
  >
    <SvgIcon
      src="/images/registration/errorIcon.svg"
      size={16}
      color="var(--Sem-icon-colour-icon-error, #9E231B)"
      sx={fieldErrorIconSx}
    />
    <Typography id={id} sx={fieldErrorTextSx}>
      {message}
    </Typography>
  </Stack>
);


// ─── CommonTextField ──────────────────────────────────────────────────────────

interface CommonTextFieldProps {
  label: string;
  disableUnderline?: boolean;
  helperText?: string;
  startIcon?: React.ReactNode;
  value?: string;
  onChange?: (val: string) => void;
  error?: boolean;
  errorMessage?: string;
  inputHtmlProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const CommonTextField: React.FC<CommonTextFieldProps> = ({
  label,
  disableUnderline = false,
  helperText,
  startIcon,
  value,
  onChange,
  error,
  errorMessage,
  inputHtmlProps
}) => {
  const [focused, setFocused] = useState(false);
  const [internalHasValue, setInternalHasValue] = useState(false);
  const errorId = useId();
  const isControlled = value !== undefined;
  const hasValue = isControlled ? value.length > 0 : internalHasValue;
  return (
    <Box>
      <TextField
        label={label}
        variant="standard"
        fullWidth
        value={isControlled ? value : undefined}
        onChange={(e) => {
          if (isControlled) {
            onChange?.(e.target.value);
          } else {
            setInternalHasValue(e.target.value.length > 0);
          }
        }}
        InputLabelProps={{ shrink: focused || hasValue }}
        InputProps={{
          disableUnderline,
          onFocus: () => setFocused(true),
          onBlur: () => setFocused(false),
          ...(startIcon && {
            startAdornment: (
              <InputAdornment position="start">{startIcon}</InputAdornment>
            )
          })
        }}
        inputProps={{
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error && errorMessage ? errorId : undefined,
          ...inputHtmlProps
        }}
        helperText={!error ? helperText : null}
        sx={{
          '& label': {
            ...textFieldLabelSx,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          '& input': textFieldInputSx,
          ...textFieldUnderlineSx,
          ...(error && textFieldErrorUnderlineSx),
          ...(startIcon && {
            '& label:not(.MuiInputLabel-shrink)': {
              transform: 'translate(34px, 20px) scale(1)',
              maxWidth: 'calc(100% - 50px)'
            }
          }),
          ...helperTextSx
        }}
      />
      {error && errorMessage && (
        <FieldError id={errorId} message={errorMessage} />
      )}
    </Box>
  );
};

// ─── IataInputField Sub-component ────────────────────────────────────────────

interface IataInputFieldProps {
  country: Country;
  value: string;
  onChange: (code: string, value: string) => void;
  error?: boolean;
  errorMessage?: string;
  submitAttempted?: boolean;
}

const IataInputField: React.FC<IataInputFieldProps> = ({
  country,
  value,
  onChange,
  error,
  errorMessage,
  submitAttempted
}) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  // IATA is optional — only invalid when the user has entered something but it's less than 8 digits.
  const isInvalid = value.length > 0 && value.length < 8;
  const showError = (submitAttempted || touched) && isInvalid;
  let internalError = '';
  if (showError) {
    internalError = t('registrationStepsData.validation.iata.minLength');
  }

  const hasError = error || !!internalError;
  const resolvedErrorMessage = errorMessage || internalError;

  return (
    <CommonTextField
      label={t('registrationStepsData.modal.iata.fieldLabel', {
        countryName: country.name
      })}
      value={value}
      onChange={(val) => {
        // Strip non-digits and cap at 8 — enforces numeric-only input.
        const sanitized = val.replace(/[^0-9]/g, '').slice(0, 8);
        onChange(country.code, sanitized);
      }}
      inputHtmlProps={{
        maxLength: 8,
        inputMode: 'numeric',
        pattern: '[0-9]*',
        onBlur: () => setTouched(true)
      }}
      startIcon={<FlagImage flag={country.flag} />}
      error={hasError}
      errorMessage={hasError ? resolvedErrorMessage : undefined}
    />
  );
};

// ─── MainIatasStep Sub-component ─────────────────────────────────────────────

interface MainIatasStepProps {
  selectedCountries: Country[];
  iataValues: Record<string, string>;
  onIataChange: (code: string, value: string) => void;
  onBack: () => void;
  onApply: () => void;
  submitAttempted?: boolean;
  canApply?: boolean;
}

const MainIatasStep: React.FC<MainIatasStepProps> = ({
  selectedCountries,
  iataValues,
  onIataChange,
  onBack,
  onApply,
  submitAttempted,
  canApply
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <>
      <DialogContent sx={iatasContentSx}>
        <Box maxWidth="1200px" width="100%" px={4} py={3} height="fit-content">
          <Grid container spacing={3} rowSpacing={5}>
            {selectedCountries.map((country) => (
              <Grid item xs={12} sm={6} md={4} key={country.code}>
                <IataInputField
                  country={country}
                  value={iataValues[country.code] ?? ''}
                  onChange={onIataChange}
                  submitAttempted={submitAttempted}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          ...footerActionsSx
        }}
      >
        <Button
          variant={isMobile ? 'text' : 'secondary'}
          onClick={onBack}
          size="md"
          aria-label={t('registrationStepsData.modal.backButton.ariaLabel')}
        >
          {t('registrationStepsData.modal.backButton.label')}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={onApply}
          disabled={!canApply}
          sx={{ p: 0, m: '0px !important' }}
        >
          {t('registrationStepsData.common.buttons.apply')}
        </Button>
      </DialogActions>
    </>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const SuperModalRegistration: React.FC<SuperModalRegistrationProps> = ({
  open,
  onClose,
  onNext,
  onOtherSelected,
  initialSelected,
  initialIataValues,
  collectIata = true
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const posState = useSelector(selectPos);

  // Build a lookup: 2-letter slice code → 3-letter i18n flag code.
  // Keys by item.code (language-agnostic) so lookups work for both en and fr.
  // Uses posState['en'] as a stable English bridge for name matching, then
  // applies normalized comparison + prefix fallback to handle slight name
  // differences (e.g. "Netherlands (the)" ↔ "Netherlands",
  //   "United States of America (the)" ↔ "United States").
  const flagCodeMap = useMemo<Map<string, string>>(() => {
    const posMapData = t('pos_map_supported_markets', { returnObjects: true }) as Array<{
      markets: Array<{ country: string }>;
    }>;
    const codeToFlag = new Map<string, string>();
    if (!Array.isArray(posMapData)) return codeToFlag;

    // Step 1: parse i18n → normalized English name → 3-letter flag code
    const nameToFlag = new Map<string, string>();
    for (const group of posMapData) {
      for (const market of group.markets ?? []) {
        const match = market.country.match(/^(.+?)\s*\(([A-Z]{3})\)$/);
        if (match) {
          nameToFlag.set(normalizeCountryName(match[1]), match[2]);
        }
      }
    }

    // Step 2: match posState[language] entries against nameToFlag by normalized name.
    // Uses the current language's descriptions so French names (e.g. "Australie") from
    // the translation data align with French descriptions from the slice (e.g. desc: "Australie").
    // Falls back to posState['en'] when no language-specific data is available.
    // Also falls back to prefix/startsWith for cases like "Pays-Bas (les)" ↔ "Pays-Bas".
    const langData: Array<{ code: string; desc: string }> = posState?.[language] ?? posState?.['en'] ?? [];
    for (const item of langData) {
      const normalizedDesc = normalizeCountryName(item.desc);
      if (nameToFlag.has(normalizedDesc)) {
        codeToFlag.set(item.code, nameToFlag.get(normalizedDesc)!);
        continue;
      }
      for (const [iName, flagCode] of Array.from(nameToFlag.entries())) {
        if (normalizedDesc.startsWith(iName) || iName.startsWith(normalizedDesc)) {
          codeToFlag.set(item.code, flagCode);
          break;
        }
      }
    }
    return codeToFlag;
  }, [t, posState, language]);

  const regions = useMemo<Region[]>(() => {
    const data: Array<{ code: string; desc: string; region: string | null }> =
      posState?.[language] ?? posState?.['en'] ?? [];
    const map = new Map<string, Country[]>();
    for (const item of data) {
      if (!item.region) continue;
      const key = item.region.toUpperCase();
      if (!map.has(key)) map.set(key, []);
      const flagCode = flagCodeMap.get(item.code) ?? item.code;
      map.get(key)!.push({
        code: item.code,
        name: item.desc,
        flag: `/images/flags/${flagCode}.png`
      });
    }
    const getRegionOrder = (name: string): number => {
      const n = name.toUpperCase();
      if (n.includes('NORTH') || n.includes('NORD')) return 0;
      if (n.includes('SOUTH') || n.includes('SUD')) return 1;
      if (n.includes('EUROPE')) return 2;
      if (n.includes('ASIA') || n.includes('ASIE')) return 3;
      if (n.includes('OCEANIA') || n.includes('OCÉANIE')) return 4;
      return 5;
    };
    const result = Array.from(map.entries())
      .map(([name, countries]) => ({
        name,
        countries: [...countries].sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => getRegionOrder(a.name) - getRegionOrder(b.name));

    // Append "Others" region at the end for items with no region (e.g. OT)
    const othersItems = data
      .filter((item) => !item.region)
      .map((item) => ({ code: item.code, name: item.desc, flag: '' }));
    if (othersItems.length > 0) {
      result.push({
        name: t('registrationStepsData.modal.othersRegion.label'),
        countries: [{ code: 'OT', name: t('registrationStepsData.modal.othersRegion.listItem'), flag: '' }]
      });
    }

    return result;
  }, [posState, language, flagCodeMap, t]);

  const allCountries = useMemo(() => regions.flatMap((r) => r.countries), [regions]);
  const totalCount = allCountries.length;

  const [step, setStep] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => closeRef.current?.focus(), 100);
    return () => clearTimeout(id);
  }, [open, step]);
  const [stepAnnouncement, setStepAnnouncement] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelected ?? [])
  );
  const [hasInteracted, setHasInteracted] = useState(false);
  const [iataValues, setIataValues] = useState<Record<string, string>>(
    initialIataValues ?? {}
  );
  const [applyAttempted, setApplyAttempted] = useState(false);
  const [search, setSearch] = useState('');

  // Reset internal state from the latest props every time the modal opens.
  // Without this, the lazy useState initializers only run once (on first mount),
  // so stale selections (e.g. OT from a previous session) persist across re-opens.
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected ?? []));
      setIataValues(initialIataValues ?? {});
      setSearch('');
      setStep(0);
      setHasInteracted(false);
      setApplyAttempted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredRegions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return regions;
    return regions.map((r) => ({
      ...r,
      countries: r.countries.filter((c) => c.name.toLowerCase().includes(q))
    })).filter((r) => r.countries.length > 0);
  }, [search, regions]);

  const toggle = useCallback((code: string) => {
    setHasInteracted(true);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (region: Region) => {
      setHasInteracted(true);
      const allChecked = region.countries.every((c) => selected.has(c.code));
      setSelected((prev) => {
        const next = new Set(prev);
        region.countries.forEach((c) =>
          allChecked ? next.delete(c.code) : next.add(c.code)
        );
        return next;
      });
    },
    [selected]
  );

  const clearAll = useCallback(() => {
    setHasInteracted(true);
    setSelected(new Set());
  }, []);

  // True only when the sole selection is the "Other" (OT) entry
  const isOtherOnlySelected = useMemo(
    () => selected.size === 1 && selected.has('OT'),
    [selected]
  );

  // Close handler — always just closes the modal without persisting changes.
  // onOtherSelected is only triggered via the Next/Apply button (handleGoToIatas),
  // never from this close/cancel path.
  const handleCloseWithOtherCheck = useCallback(() => {
    // Close without persisting changes - reset to initial state
    setSelected(new Set(initialSelected ?? []));
    setIataValues(initialIataValues ?? {});
    setStep(0);
    setHasInteracted(false);
    setApplyAttempted(false);
    onClose();
  }, [onClose, initialSelected, initialIataValues]);

  const handleBack = useCallback(() => {
    setApplyAttempted(false);
    setStep(0);
    setStepAnnouncement(
      t('registrationStepsData.modal.step1.stepAnnouncement') as string
    );
  }, [t]);
  const handleGoToIatas = useCallback(() => {
    // Non travel-agency sellers don't provide IATA numbers — apply with null IATA
    if (!collectIata) {
      const items: AppliedPOSItem[] = allCountries
        .filter((c) => selected.has(c.code))
        .map((c) => ({ ...c, iata: '' }));
      onNext(items);
      return;
    }
    setStep(1);
    setStepAnnouncement(
      t('registrationStepsData.modal.step2.stepAnnouncement') as string
    );
  }, [t, collectIata, allCountries, selected, onNext]);

  const handleIataChange = useCallback((code: string, value: string) => {
    // Step 2 edits must also unlock Apply, even without additional country toggles.
    setHasInteracted(true);
    setIataValues((prev) => ({ ...prev, [code]: value }));
  }, []);

  const handleApply = useCallback(() => {
    // Keep "Other" flow behavior, but only commit it from Apply.
    if (isOtherOnlySelected) {
      onOtherSelected?.();
      onClose();
      return;
    }

    const hasInvalid = allCountries.some(
      (c) =>
        selected.has(c.code) &&
        (iataValues[c.code] ?? '').length > 0 &&
        (iataValues[c.code] ?? '').length < 8
    );
    if (hasInvalid) {
      setApplyAttempted(true);
      return;
    }
    const items: AppliedPOSItem[] = allCountries.filter((c) =>
      selected.has(c.code)
    ).map((c) => ({ ...c, iata: iataValues[c.code] ?? '' }));
    onNext(items);
  }, [isOtherOnlySelected, onOtherSelected, onClose, onNext, selected, iataValues, allCountries]);

  const selectedCountries = allCountries.filter((c) => selected.has(c.code));

  const filteredCount = useMemo(
    () => filteredRegions.reduce((n, r) => n + r.countries.length, 0),
    [filteredRegions]
  );
  
  const getSearchAnnouncement = useCallback(
    (searchTerm: string, count: number): string => {
      if (!searchTerm.trim()) return '';

      return count === 1
        ? t('registrationStepsData.modal.search.resultsSingular', { count })
        : t('registrationStepsData.modal.search.resultsPlural', { count });
    },
    [t]
  );

  const [searchAnnouncement, setSearchAnnouncement] = useState('')
  useEffect(
    ()=>{
      setSearchAnnouncement('')
      const timeoutId= setTimeout(()=>{ 
        setSearchAnnouncement(getSearchAnnouncement(search,filteredCount)
      )},100)
     return ()=>{
      clearTimeout(timeoutId)
     }
    },
    [search,filteredCount,getSearchAnnouncement]
  )

  const searchInputRef = useRef<HTMLInputElement>(null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-modal={true}
      TransitionComponent={SlideUp}
      aria-labelledby="pos-modal-title"
      aria-describedby={step === 1 ? 'pos-modal-subtitle' : undefined}
      PaperProps={{ sx: dialogPaperSx, tabIndex: -1 }}
      sx={dialogSx}
      disableRestoreFocus
    >
      <VisuallyHidden role="status" aria-live="polite" aria-atomic>
        {stepAnnouncement}
      </VisuallyHidden>

      <ModalHeader
        closeRef={closeRef}
        title={
          step === 0
            ? t('registrationStepsData.modal.step1.title')
            : t('registrationStepsData.modal.step2.title')
        }
        subtitle={
          step === 1
            ? t('registrationStepsData.modal.step2.subtitle')
            : undefined
        }
        onClose={handleCloseWithOtherCheck}
      />

      {step === 0 ? (
        <>
          <DialogContent sx={step1ContentSx}>
            <Grid
              container
              maxWidth={{ md: '1200px', xs: '704px' }}
              width="100%"
              gap={3}
              flexWrap={{ xs: 'wrap', md: 'nowrap' }}
              alignContent={{ xs: 'flex-start', md: 'stretch' }}
              overflow={{ xs: 'visible', md: 'hidden' }}
              mx={{ xs: 4 }}
            >
              {/* Left: search + country list */}
              <Grid
                item
                xs={12}
                md={8}
                gap={4}
                py={0}
                order={{ xs: 2, md: 1 }}
                sx={leftPanelSx}
              >
                <TextField
                  size="small"
                  inputRef={searchInputRef}
                  placeholder={t(
                    'registrationStepsData.modal.search.placeholder'
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{
                    'aria-label': t(
                      'registrationStepsData.modal.search.ariaLabel'
                    )
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon src='/images/registration/search.svg' color='#1460AA' size={23} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t(
                            'registrationStepsData.modal.search.clearAriaLabel'
                          )}
                          onClick={() => {setSearch(''); searchInputRef.current?.focus()}}
                          edge="end"
                          size="small"
                        >
                          <SvgIcon src='/images/registration/closeButton.svg' color='#1460AA' size={12} />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined
                  }}
                  sx={searchFieldSx}
                  fullWidth
                />

                <VisuallyHidden role="status" aria-live="polite" aria-atomic>
                  {searchAnnouncement}
                </VisuallyHidden>

                <Box
                  sx={scrollableListSx}
                  role="region"
                  aria-label={t(
                    'registrationStepsData.modal.search.countryListAriaLabel'
                  )}
                >
                  {filteredRegions.map((region) => {
                    const allChecked = region.countries.every((c) =>
                      selected.has(c.code)
                    );
                    const regionId = `region-${region.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
                    return (
                      <Box
                        key={region.name}
                        gap={3}
                        sx={regionBoxSx}
                        role="group"
                        aria-labelledby={regionId}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography id={regionId} sx={regionNameSx}>
                            {region.name}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleSelectAll(region)}
                            aria-label={
                              allChecked
                                ? t(
                                  'registrationStepsData.modal.regionActions.deselectAllAriaLabel',
                                  { regionName: region.name }
                                )
                                : t(
                                  'registrationStepsData.modal.regionActions.selectAllAriaLabel',
                                  { regionName: region.name }
                                )
                            }
                            sx={linkButtonSx}
                          >
                            {allChecked
                              ? t(
                                'registrationStepsData.modal.regionActions.deselectAll'
                              )
                              : t(
                                'registrationStepsData.modal.regionActions.selectAll',
                                { count: region.countries.length }
                              )}
                          </Button>
                        </Stack>

                        <Box
                          role="list"
                          display={'flex'}
                          flexDirection={'column'}
                          gap={2}
                        >
                          {region.countries.map((country) => (
                            <Box role="listitem" key={country.code}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                component="label"
                              >
                                <Checkbox
                                  size="small"
                                  checked={selected.has(country.code)}
                                  onChange={() => toggle(country.code)}
                                  inputProps={{ 'aria-labelledby': `${country.code}` }}
                                  sx={checkboxSx}
                                />
                                <FlagImage aria-hidden={true} flag={country.flag} />
                                <Typography id={`${country.code}`} aria-hidden={true} sx={countryNameSx}>
                                  {country.name}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* Right: selected chips panel */}
              <Grid
                item
                xs={12}
                md={4}
                gap={4}
                p={4}
                pb={0}
                order={{ xs: 1, md: 2 }}
                sx={rightPanelSx}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    aria-live="polite"
                    aria-atomic={true}
                    sx={selectedPanelTitleSx}
                  >
                    {t('registrationStepsData.modal.selectedPanel.title', {
                      count: selected.size
                    })}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={clearAll}
                    disabled={selected.size === 0}
                    aria-label={t(
                      'registrationStepsData.modal.selectedPanel.clearAllAriaLabel'
                    )}
                    sx={linkButtonSx}
                  >
                    {t('registrationStepsData.modal.selectedPanel.clearAll')}
                  </Button>
                </Stack>

                {selectedCountries.length ? (
                  <Box
                    component="ul"
                    aria-live="polite"
                    aria-relevant="additions removals"
                    sx={{ ...chipContainerSx, listStyle: 'none' }}
                  >
                    {selectedCountries.map((c, idx) => (
                      <Box component="li" key={c.code} sx={{ listStyle: 'none' }}>
                        <Chip
                          label={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              p={0}
                            >
                              <FlagImage flag={c.flag} />
                              <span style={{...chipLabelStyle,
                                ...(isMobile && {
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  display: 'block',
                                  minWidth: 0
                                })
                              }}>{c.name}</span>
                            </Stack>
                          }
                          onDelete={(e) => {
                            const li = (e.target as HTMLElement).closest('li');
                            const nextLi = li?.nextElementSibling ?? li?.previousElementSibling;
                            const btn = nextLi?.querySelector<HTMLElement>('[tabindex="0"]');
                            if (btn) btn.focus();
                            toggle(c.code);
                          }}
                          deleteIcon={
                            <Box tabIndex={0} onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const li = (e.target as HTMLElement).closest('li');
                                const nextLi = li?.nextElementSibling ?? li?.previousElementSibling;
                                const btn = nextLi?.querySelector<HTMLElement>('[tabindex="0"]');
                                if (btn) btn.focus();
                                toggle(c.code);
                              }
                            }}
                            aria-label={t(
                                  'registrationStepsData.modal.selectedPanel.removeCountryAriaLabel',
                                  { countryName: c.name }
                                )} 
                            sx={{ '&:focus-visible': { outline: '2px solid black', outlineOffset: '2px' } }}>
                              <SvgIcon
                                src='/images/registration/closeButton.svg'
                                color='#002345'
                                size={14}
                                role="button"
                                
                              />
                            </Box>
                          }
                          size="small"
                          variant="outlined"
                          tabIndex={-1}
                          sx={chipSx}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box></Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={footerActionsSx}>
            {!isMobile ? (
              <Typography
                aria-live="polite"
                aria-atomic="true"
                sx={footerCountTextSx}
              >
                {t('registrationStepsData.modal.footer.selectionCount', {
                  selected: selected.size,
                  total: totalCount
                })}
              </Typography>
            ) : null}
            <Button
              variant="primary"
              size="md"
              onClick={handleGoToIatas}
              disabled={collectIata ? (!hasInteracted && selected.size === 0) : !hasInteracted}
            >
              {collectIata
                ? t('registrationStepsData.common.buttons.next')
                : t('registrationStepsData.common.buttons.apply')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <MainIatasStep
          selectedCountries={selectedCountries.filter((c) => c.code !== 'OT')}
          iataValues={iataValues}
          onIataChange={handleIataChange}
          onBack={handleBack}
          onApply={handleApply}
          submitAttempted={applyAttempted}
          canApply = {hasInteracted}
        />
      )}
    </Dialog>
  );
};

export default SuperModalRegistration;
